import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Form, Row, Col, Alert, Spinner, Card, Modal, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCheck, faTimes, faEye, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { getExtOrders, getExtOrderDetails, approveExtOrder, rejectExtOrder, approveExtOrderItem, rejectExtOrderItem } from '../services/api';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import './Orders.css';

function ExtOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState({
    items: []
  });
  const [approvalLoading, setApprovalLoading] = useState(false);
  
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0], // Last 30 days
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  const [filters, setFilters] = useState({
    status: '',
    approvalStatus: ''
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getExtOrders(dateRange.startDate, dateRange.endDate, filters);
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching external orders:', error);
      setError('Failed to fetch external orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [dateRange, filters]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyDateFilter = () => {
    if (dateRange.startDate <= dateRange.endDate) {
      fetchOrders();
    } else {
      toast.error("Start date must be before end date");
    }
  };

  const handleOrderClick = async (order) => {
    try {
      setSelectedOrder(order);
      
      // Load order details
      const details = await getExtOrderDetails(order.id);
      setOrderDetails(details);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Failed to load order details:", error);
      toast.error(`Failed to load order details: ${error.message || 'Unknown error'}`);
    }
  };

  const handleOrderApproval = async (orderId, action, reason = '') => {
    try {
      setApprovalLoading(true);
      
      if (action === 'approve') {
        await approveExtOrder(orderId);
        toast.success('Order approved successfully');
      } else {
        await rejectExtOrder(orderId, reason);
        toast.success('Order rejected successfully');
      }
      
      // Refresh orders list
      await fetchOrders();
      
      // Update order details if modal is open
      if (selectedOrder && selectedOrder.id === orderId) {
        const updatedDetails = await getExtOrderDetails(orderId);
        setOrderDetails(updatedDetails);
        setSelectedOrder(prev => ({ ...prev, approval_status: action === 'approve' ? 'approved' : 'rejected' }));
      }
    } catch (error) {
      console.error(`Error ${action}ing order:`, error);
      toast.error(`Failed to ${action} order: ${error.message || 'Unknown error'}`);
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleItemApproval = async (itemId, action, quantity = null, reason = '') => {
    try {
      setApprovalLoading(true);
      
      if (action === 'approve') {
        await approveExtOrderItem(itemId, quantity);
        toast.success('Item approved successfully');
      } else {
        await rejectExtOrderItem(itemId, reason);
        toast.success('Item rejected successfully');
      }
      
      // Refresh order details
      const updatedDetails = await getExtOrderDetails(selectedOrder.id);
      setOrderDetails(updatedDetails);
    } catch (error) {
      console.error(`Error ${action}ing item:`, error);
      toast.error(`Failed to ${action} item: ${error.message || 'Unknown error'}`);
    } finally {
      setApprovalLoading(false);
    }
  };

  // Format date to MM/DD/YY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('MM/DD/YY');
  };

  // Format weight for display
  const formatWeight = (weight) => {
    if (!weight) return '-';
    return `${parseFloat(weight).toLocaleString()} lbs`;
  };

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'danger';
      case 'under_review': return 'info';
      case 'processing': return 'primary';
      case 'shipped': return 'success';
      case 'delivered': return 'secondary';
      default: return 'secondary';
    }
  };

  const getApprovalStatusVariant = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'danger';
      default: return 'secondary';
    }
  };

  const getItemStatusVariant = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'danger';
      case 'allocated': return 'info';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Container fluid className="px-2">
      <Card className="shadow-sm">
        <Card.Header className="py-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <h5 className="m-0 font-weight-bold text-primary">
              External Orders
              <small className="ms-2 text-muted" style={{ fontSize: '0.7rem' }}>B2B Portal Orders - Click to approve/reject</small>
            </h5>
            <div className="d-flex gap-3 flex-wrap mt-2 mt-sm-0">
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">From:</Form.Label>
                <Form.Control
                  type="date"
                  name="startDate"
                  value={dateRange.startDate}
                  onChange={handleDateChange}
                  size="sm"
                />
              </div>
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">To:</Form.Label>
                <Form.Control
                  type="date"
                  name="endDate"
                  value={dateRange.endDate}
                  onChange={handleDateChange}
                  size="sm"
                />
              </div>
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">Status:</Form.Label>
                <Form.Select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  size="sm"
                  style={{ width: '150px' }}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="processing">Processing</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                </Form.Select>
              </div>
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">Approval:</Form.Label>
                <Form.Select
                  name="approvalStatus"
                  value={filters.approvalStatus}
                  onChange={handleFilterChange}
                  size="sm"
                  style={{ width: '130px' }}
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </Form.Select>
              </div>
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleApplyDateFilter}
              >
                <FontAwesomeIcon icon={faSearch} className="me-1" />
                Search
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger" className="m-3">
              {error}
            </Alert>
          )}
          
          {orders.length === 0 ? (
            <Alert variant="info" className="m-3">
              No external orders found for the selected criteria.
            </Alert>
          ) : (
            <div style={{ height: 'calc(100vh - 200px)', minHeight: '400px', overflowY: 'auto' }}>
              <Table hover size="sm" className="mb-0">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>Order #</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>End User</th>
                    <th>Items</th>
                    <th>Total Weight</th>
                    <th>Status</th>
                    <th>Approval</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <tr 
                      key={order.id} 
                      onClick={() => handleOrderClick(order)} 
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{order.order_number}</td>
                      <td>{formatDate(order.created_date)}</td>
                      <td>{order.customer_name || '-'}</td>
                      <td>{order.end_user_name || '-'}</td>
                      <td>{order.total_items || 0}</td>
                      <td>{formatWeight(order.total_weight)}</td>
                      <td>
                        <Badge bg={getStatusVariant(order.status)}>
                          {order.status?.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getApprovalStatusVariant(order.approval_status)}>
                          {order.approval_status?.toUpperCase()}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Order Detail Modal */}
      <Modal size="xl" show={showDetailModal} onHide={() => setShowDetailModal(false)}>
        <Modal.Header closeButton className="py-2">
          <Modal.Title>External Order Details - {selectedOrder?.order_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <Row className="g-2 mb-2">
            <Col md={4}>
              <Card className="shadow-sm h-100">
                <Card.Header className="py-1">
                  <h6 className="mb-0">Order Information</h6>
                </Card.Header>
                <Card.Body className="py-2 px-3">
                  <p className="mb-1 small"><strong>Order #:</strong> {selectedOrder?.order_number}</p>
                  <p className="mb-1 small"><strong>Date:</strong> {formatDate(selectedOrder?.created_date)}</p>
                  <p className="mb-1 small"><strong>Status:</strong> 
                    <Badge bg={getStatusVariant(selectedOrder?.status)} className="ms-1">
                      {selectedOrder?.status?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </p>
                  <p className="mb-1 small"><strong>Approval:</strong> 
                    <Badge bg={getApprovalStatusVariant(selectedOrder?.approval_status)} className="ms-1">
                      {selectedOrder?.approval_status?.toUpperCase()}
                    </Badge>
                  </p>
                  <p className="mb-1 small"><strong>Reference:</strong> {selectedOrder?.customer_reference || 'N/A'}</p>
                  <p className="mb-0 small"><strong>Total Weight:</strong> {formatWeight(selectedOrder?.total_weight)}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm h-100">
                <Card.Header className="py-1">
                  <h6 className="mb-0">End User Information</h6>
                </Card.Header>
                <Card.Body className="py-2 px-3">
                  <p className="mb-1 small"><strong>Name:</strong> {orderDetails.end_user_name || 'N/A'}</p>
                  <p className="mb-1 small"><strong>Email:</strong> {orderDetails.end_user_email || 'N/A'}</p>
                  <p className="mb-0 small"><strong>Phone:</strong> {orderDetails.end_user_phone || 'N/A'}</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm h-100">
                <Card.Header className="py-1">
                  <h6 className="mb-0">Shipping Information</h6>
                </Card.Header>
                <Card.Body className="py-2 px-3">
                  <p className="mb-1 small"><strong>Name:</strong> {orderDetails.shipping_name || 'N/A'}</p>
                  <p className="mb-1 small"><strong>Company:</strong> {orderDetails.shipping_company || 'N/A'}</p>
                  <p className="mb-1 small">{orderDetails.shipping_address || ''}</p>
                  <p className="mb-1 small">
                    {[orderDetails.shipping_city, orderDetails.shipping_state, orderDetails.shipping_zip]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  <p className="mb-0 small"><strong>Phone:</strong> {orderDetails.shipping_phone || 'N/A'}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {orderDetails.special_instructions && (
            <Card className="shadow-sm mb-2">
              <Card.Header className="py-1">
                <h6 className="mb-0">Special Instructions</h6>
              </Card.Header>
              <Card.Body className="py-2 px-3">
                <p className="mb-0 small">{orderDetails.special_instructions}</p>
              </Card.Body>
            </Card>
          )}

          <Card className="shadow-sm mb-2">
            <Card.Header className="py-2 d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Order Items</h6>
              {selectedOrder?.approval_status === 'pending' && (
                <div>
                  <Button 
                    variant="success" 
                    size="sm" 
                    className="me-2"
                    disabled={approvalLoading}
                    onClick={() => handleOrderApproval(selectedOrder.id, 'approve')}
                  >
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    Approve All
                  </Button>
                  <Button 
                    variant="danger" 
                    size="sm"
                    disabled={approvalLoading}
                    onClick={() => {
                      const reason = prompt('Enter rejection reason:');
                      if (reason) {
                        handleOrderApproval(selectedOrder.id, 'reject', reason);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Reject All
                  </Button>
                </div>
              )}
            </Card.Header>
            <Card.Body className="p-0" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <Table size="sm" className="m-0">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>Item #</th>
                    <th>Name</th>
                    <th>Requested Qty</th>
                    <th>Approved Qty</th>
                    <th>Unit Weight</th>
                    <th>Total Weight</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orderDetails.items?.map((item) => (
                    <tr key={item.id}>
                      <td>{item.item_number}</td>
                      <td>{item.item_name}</td>
                      <td>{item.requested_quantity}</td>
                      <td>{item.approved_quantity || '-'}</td>
                      <td>{formatWeight(item.unit_weight)}</td>
                      <td>{formatWeight(item.total_weight)}</td>
                      <td>
                        <Badge bg={getItemStatusVariant(item.item_status)}>
                          {item.item_status?.toUpperCase()}
                        </Badge>
                      </td>
                      <td>
                        {item.item_status === 'pending' && (
                          <div className="d-flex gap-1">
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              disabled={approvalLoading}
                              onClick={() => {
                                const qty = prompt(`Enter approved quantity (max: ${item.requested_quantity}):`);
                                if (qty && parseInt(qty) > 0 && parseInt(qty) <= item.requested_quantity) {
                                  handleItemApproval(item.id, 'approve', parseInt(qty));
                                } else if (qty) {
                                  toast.error('Invalid quantity');
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </Button>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              disabled={approvalLoading}
                              onClick={() => {
                                const reason = prompt('Enter rejection reason:');
                                if (reason) {
                                  handleItemApproval(item.id, 'reject', null, reason);
                                }
                              }}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          </div>
                        )}
                        {item.rejection_reason && (
                          <small className="text-danger d-block">{item.rejection_reason}</small>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Modal.Body>
      </Modal>
    </Container>
  );
}

export default ExtOrders; 